<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useSupportTicket } from '@/composables/useSupportTicket'
import { useSupportUnreadCount } from '@/composables/useSupportUnreadCount'
import type { StoreSupportMessagePayload } from '@/types/support'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import SupportThreadView from '@/components/organizer/support/SupportThreadView.vue'

const { t } = useI18n()
const route = useRoute()
const { currentOrganisation } = useAuth()

const orgId = computed<number>(() => currentOrganisation.value?.id ?? 0)
const ticketId = computed<number>(() => Number(route.params.ticketId))

const { ticket, loading, submitting, error, closedError, load, reply, close, reopen } = useSupportTicket()
const { fetch: fetchUnreadCount } = useSupportUnreadCount()

onMounted(async () => {
  await load(orgId.value, ticketId.value)
  // Refresh unread count after opening the thread (clears the flag server-side)
  void fetchUnreadCount(orgId.value)
})

async function handleReply(payload: { body: string; attachmentIds: number[] }): Promise<void> {
  const replyPayload: StoreSupportMessagePayload = {
    body: payload.body,
    attachment_ids: payload.attachmentIds.length > 0 ? payload.attachmentIds : undefined,
  }
  await reply(orgId.value, ticketId.value, replyPayload)
}

async function handleClose(): Promise<void> {
  await close(orgId.value, ticketId.value)
}

async function handleReopen(): Promise<void> {
  await reopen(orgId.value, ticketId.value)
}
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Back link -->
      <RouterLink
        to="/organizer/support"
        class="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors mb-6"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('support.thread.back') }}
      </RouterLink>

      <!-- Loading skeleton -->
      <div v-if="loading" class="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading">
        <div class="h-8 bg-slate-800 rounded w-96" />
        <div class="h-4 bg-slate-800 rounded w-64" />
        <div class="flex gap-6">
          <div class="w-56 h-64 bg-slate-800 rounded-xl" />
          <div class="flex-1 space-y-4">
            <div class="h-24 bg-slate-800 rounded-2xl" />
            <div class="h-24 bg-slate-800 rounded-2xl" />
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div
        v-else-if="error"
        class="rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-4 text-sm text-red-400"
      >
        {{ t(error) }}
      </div>

      <!-- Thread -->
      <template v-else-if="ticket">
        <!-- Ticket header -->
        <div class="mb-6">
          <h1 class="text-3xl font-bold text-white break-words">{{ ticket.subject }}</h1>
          <p class="mt-1 text-sm text-slate-400">
            {{ t(`support.category.${ticket.category}`) }}
            &middot;
            {{ t('support.thread.ticket_ref', { id: ticket.id }) }}
            &middot;
            {{ t('support.thread.opened', { date: new Date(ticket.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) }) }}
          </p>
        </div>

        <!-- Closed-reply error banner -->
        <div
          v-if="closedError"
          class="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 px-5 py-3 text-sm text-amber-400"
        >
          {{ t(closedError) }}
        </div>

        <!-- Thread view -->
        <SupportThreadView
          :ticket="ticket"
          :submitting="submitting"
          :organisation-id="orgId"
          @reply="handleReply"
          @close="handleClose"
          @reopen="handleReopen"
        />
      </template>
    </main>
  </OrganizerLayout>
</template>
